'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowRight, CheckCircle2, Circle } from 'lucide-react';
import { WeekData } from '@/lib/curriculum';
import { WeekProgress, toggleDay } from '@/lib/storage';
import { PHASE_META, DAY_SHORT } from '@/lib/curriculum';

interface CurrentWeekCardProps {
  weekData: WeekData | undefined;
  weekProgress: WeekProgress | null;
  currentDayIndex: number;
  onProgressChange: () => void;
}

export default function CurrentWeekCard({ weekData, weekProgress, currentDayIndex, onProgressChange }: CurrentWeekCardProps) {
  if (!weekData) return null;

  const phase = PHASE_META[weekData.phase];
  const daysComplete = weekProgress?.daysComplete ?? [false, false, false, false, false];

  const handleToggleDay = (dayIdx: number) => {
    toggleDay(weekData.weekNum, dayIdx);
    onProgressChange();
  };

  const completedCount = daysComplete.filter(Boolean).length;

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        gridColumn: 'span 1',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span
              style={{
                fontSize: '11px',
                fontFamily: 'IBM Plex Mono, monospace',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: phase.color,
                color: phase.textColor,
                border: `1px solid ${phase.border}`,
                letterSpacing: '0.04em',
              }}
            >
              WK {weekData.weekNum}
            </span>
            <span
              style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: phase.color,
                color: phase.textColor,
                border: `1px solid ${phase.border}`,
              }}
            >
              {phase.label}
            </span>
          </div>
          <h3
            style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              fontSize: '16px',
              fontWeight: 400,
              color: 'var(--text-primary)',
              lineHeight: 1.3,
            }}
          >
            {weekData.title}
          </h3>
        </div>
        <Link
          href={`/week-detail-view?week=${weekData.weekNum}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border)',
            color: 'var(--text-muted)',
            textDecoration: 'none',
            transition: 'all 150ms ease',
            flexShrink: 0,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--accent-glow)';
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-border)';
            (e.currentTarget as HTMLElement).style.color = 'var(--accent)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-elevated)';
            (e.currentTarget as HTMLElement).style.borderColor = 'var(--border)';
            (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
          }}
        >
          <ArrowRight size={14} />
        </Link>
      </div>

      {/* Today's task */}
      <div
        style={{
          padding: '10px 12px',
          backgroundColor: 'var(--bg-elevated)',
          borderRadius: '8px',
          border: '1px solid var(--border-subtle)',
        }}
      >
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace', marginBottom: '4px' }}>
          TODAY — {DAY_SHORT[currentDayIndex].toUpperCase()}
        </div>
        <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.4 }}>
          {weekData.days[currentDayIndex]?.topic}
        </div>
      </div>

      {/* Day checkboxes */}
      <div style={{ display: 'flex', gap: '6px' }}>
        {DAY_SHORT.map((day, idx) => {
          const done = daysComplete[idx];
          const isToday = idx === currentDayIndex;
          return (
            <button
              key={`day-btn-${weekData.weekNum}-${idx}`}
              onClick={() => handleToggleDay(idx)}
              aria-label={`Mark ${day} ${done ? 'incomplete' : 'complete'}`}
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                padding: '6px 4px',
                borderRadius: '6px',
                border: `1px solid ${isToday ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
                backgroundColor: done
                  ? 'var(--accent-glow)'
                  : isToday
                    ? 'var(--bg-elevated)'
                    : 'transparent',
                cursor: 'pointer',
                transition: 'all 150ms ease',
                color: done ? 'var(--accent)' : isToday ? 'var(--text-primary)' : 'var(--text-muted)',
              }}
            >
              {done ? <CheckCircle2 size={14} /> : <Circle size={14} />}
              <span style={{ fontSize: '10px', fontFamily: 'IBM Plex Mono, monospace' }}>{day}</span>
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'IBM Plex Mono, monospace' }}>
        {completedCount}/5 days complete {weekProgress?.assessmentPassed ? '· Assessment ✓' : ''}
      </div>
    </div>
  );
}